import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { KompisMiniConfigurationSummary } from '@inter-ikea-kompis/react-components';
import { ButtonSizeEnum } from '@inter-ikea-kompis/component-button';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import styled from 'styled-components';
import {
	ShoppingItem,
	Settings,
	Platform,
	DeviceType,
	Orientation,
	PopOverSceneItemData,
	PopOver,
	RangeSettings,
	SofaConfiguration,
	WriteDirection,
	AppView,
	Lightbox,
} from 'sofa-reborn-react';
import { RootState, AppDispatch } from '../store';
import {
	setAppView,
	setCurrentLightbox,
	setFooterHeight,
	setTotalPriceVoice,
} from '../store/application/actions';
import VpcThunk from '../store/application/thunk/VpcThunk';
import { hidePopOver, setMissingCoverSectionHasBeenShown } from '../store/popOver/actions';
import { resetCameraRotation, setKeepSceneHeight } from '../store/scene/actions';
import {
	screenshotPending,
	screenshotSuccess,
	setScreenshotSpinner,
	setUpptackaSaveFail,
} from '../store/checkout';
import ActionButtonsContainer from '../containers/ActionButtonsContainer';
import { showPopOver } from '../store/popOver/actions';
import { ProductService, KompisDefaultFields, VpcService } from '@inter-ikea-kompis/services';
import { IDexfSettings, IShoppingProduct, ITranslations } from '@inter-ikea-kompis/types';
import { localeService, configurationService } from '../services';
import { ProductData } from '@ikea-aoa/component-view-3d';
import { Groups, ModelData } from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import {
	TotalPriceCalculator,
	PriceUtility,
	PriceFormatterUtility,
} from '@inter-ikea-kompis/utilities';
import { MiniConfigurationSummaryModalEnum } from '@inter-ikea-kompis/component-mini-configuration-summary';

let nextClicked = false;
const StyledFooter = styled.div<{
	orientation: Orientation;
	deviceType: DeviceType;
	platform: Platform;
	writeDirection: WriteDirection;
}>`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: ${(props) => (props.deviceType === 'mobile' ? 'flex-end' : 'space-between')};
	padding: ${(props) =>
		props.platform === 'kiosk'
			? '16px'
			: props.deviceType === 'mobile' && props.orientation === 'landscape'
			? props.writeDirection === 'ltr'
				? '0 16px 8px 16px'
				: '0 16px 8px 0'
			: props.orientation === 'portrait'
			? '16px 0'
			: '0 16px 16px 16px'};
	width: ${(props) =>
		props.orientation === 'portrait'
			? `100%`
			: props.deviceType === 'desktop'
			? props.platform === 'kiosk'
				? 'calc(100% - 468px)'
				: 'calc(100% - 368px)'
			: props.deviceType === 'mobile' &&
			  props.orientation === 'landscape' &&
			  props.writeDirection === 'ltr'
			? `calc(100% - 146px)`
			: `auto`};
	align-self: ${(props) =>
		props.deviceType === 'desktop' && props.writeDirection === 'rtl' ? 'flex-end' : 'auto'};
`;

const FooterContainer: FC = () => {
	const products = useSelector<RootState, ProductData>((state) => state.products.products);
	const shoppingItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.sofaConfiguration.shoppingItems
	);
	const additionalCoverItems = useSelector<RootState, ShoppingItem[]>(
		(state) => state.cover.additionalCoverItems
	);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const appView = useSelector<RootState, AppView>((state) => state.application.appView);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const unfinishedSections = useSelector<RootState, PopOverSceneItemData[]>(
		(state) => state.missing.unfinishedSections
	);
	const missingCoverSections = useSelector<RootState, PopOverSceneItemData[]>(
		(state) => state.missing.missingCoverSections
	);
	const backrestsMissingCushion = useSelector<RootState, boolean>(
		(state) => state.scene.backrestsMissingCushion as boolean
	);
	const noValidConnection = useSelector<RootState, PopOver>(
		(state) => state.popOver.noValidConnection
	);
	const isInUpptackaPlatform = useSelector<RootState, boolean>(
		(state) => state.application.isInUpptackaPlatform
	);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const sofaConfiguration = useSelector<RootState, SofaConfiguration>(
		(state) => state.sofaConfiguration.sofaConfiguration
	);
	const totalPriceVoice = useSelector<RootState, string | null>(
		(state) => state.application.totalPriceVoice
	);
	const screenshot = useSelector<RootState, string>((state) => state.checkout.screenshot as string);
	const screenshotIsPending = useSelector<RootState, boolean | null>(
		(state) => state.checkout.screenshotPending
	);
	const sofaModels = useSelector<RootState, ModelData>((state) => state.models.sofaModels);
	const sofaGroups = useSelector<RootState, Groups>((state) => state.models.sofaGroups);

	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const enableMiniConfigurationSummary =
		deviceType === 'mobile' ? appView === 'build-view' && currentLightbox === 'none' : true;
	const dispatch = useDispatch<AppDispatch>();

	const [shoppingProducts, setShoppingProducts] = useState<IShoppingProduct[] | []>([]);
	const [visibleModal, setVisibleModal] = useState<
		MiniConfigurationSummaryModalEnum | undefined | null
	>(null);
	const [upptackaLoading, setUpptackaLoading] = useState<boolean>(false);

	async function getShoppingProducts(): Promise<IShoppingProduct[]> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const shoppingProducts: IShoppingProduct[] = await productService.getShoppingProducts(
			shoppingItems,
			KompisDefaultFields
		);

		if (coverOnly) {
			const articleItems: ShoppingItem[] = [];
			shoppingProducts.forEach((prod) => {
				if (prod.product?.itemId?.includes('ART')) {
					articleItems.push({
						id: prod.product.itemId.replace('ART-', ''),
						quantity: prod.quantity,
						type: 'ART',
					});
				} else {
					prod.product.content?.child?.forEach((child) => {
						articleItems.push({
							id: child.itemId.replace('ART-', ''),
							quantity: child.quantity * prod.quantity,
							type: 'ART',
						});
					});
				}
			});

			const articleProducts = await productService.getShoppingProducts(
				articleItems,
				KompisDefaultFields
			);

			const filteredCoverProducts = articleProducts.filter(
				(prod) => prod.product.valid && prod.product.content?.mainTypeCode === '00062'
			);

			setShoppingProducts(filteredCoverProducts);
			return filteredCoverProducts;
		} else {
			const filteredShoppingProducts = shoppingProducts.filter(
				(shoppingProduct) => shoppingProduct.product.valid
			);
			setShoppingProducts(filteredShoppingProducts);
			return filteredShoppingProducts;
		}
	}

	function timeoutAfter(seconds): Promise<null> {
		return new Promise((_, reject) => {
			setTimeout(() => {
				reject(new Error('request timed-out'));
			}, seconds * 1000);
		});
	}

	const onNextClick = (
		dispatch: AppDispatch,
		platform: Platform,
		unfinishedSections: PopOverSceneItemData[],
		missingCoverSections: PopOverSceneItemData[],
		backrestsMissingCushion: boolean,
		noValidConnection: PopOver,
		isInUpptackaPlatform: boolean,
		products: ProductData
	): void => {
		if (unfinishedSections.length > 0) {
			if (noValidConnection.showPopOver) {
				dispatch(hidePopOver({ type: 'no-valid-connection' }));
			}
			const { screenPosition } = unfinishedSections[0];
			dispatch(
				showPopOver({
					type: 'unfinished-section',
					top: screenPosition.y,
					left: screenPosition.x,
				})
			);
			return;
		} else if (missingCoverSections.length > 0) {
			const { screenPosition } = missingCoverSections[0];
			dispatch(setMissingCoverSectionHasBeenShown(false));
			dispatch(
				showPopOver({
					type: 'missing-cover-section',
					top: screenPosition.y,
					left: screenPosition.x,
				})
			);
			return;
		}

		if (isInUpptackaPlatform) {
			setUpptackaLoading(true);
			nextClicked = true;
			setTimeout(() => {
				dispatch(screenshotPending());
			}, 300);
			return;
		}

		if (platform == 'kiosk') {
			const vpcThunk = new VpcThunk();
			dispatch(vpcThunk.fetch(false));
			dispatch(screenshotSuccess(null));
		}

		dispatch(
			hidePopOver({
				type: 'all',
			})
		);
		dispatch(
			hidePopOver({
				type: 'placement-information',
			})
		);
		if (!backrestsMissingCushion) {
			dispatch(resetCameraRotation(true));
			dispatch(setScreenshotSpinner(true));
			setTimeout(() => {
				// dispatching delayed to increase the chances of babylon getting a frame ready...
				dispatch(screenshotPending());
			}, 300);
		}

		if (
			Object.values(products).some((product) => product.filter.family === 'CUSHION') &&
			backrestsMissingCushion
		) {
			dispatch(setCurrentLightbox('confirm-cushion'));
			return;
		}
		dispatch(setKeepSceneHeight(true));
		dispatch(setAppView('summary-page'));
	};

	const onModalOpen = (e: CustomEvent): void => {
		setVisibleModal(e.detail.visibleModal);
	};

	const onModalClose = (): void => {
		setVisibleModal(null);
	};

	useEffect(() => {
		if (isInUpptackaPlatform && sofaConfiguration && nextClicked && !screenshotIsPending) {
			const sendUpptackaEvent = async () => {
				try {
					const vpcService = new VpcService({
						locale: settings.locale,
						localizationPrefixApplication: settings.localizationPrefixApplication,
						localizationEndpoints: settings.localizationEndpoints,
						applicationName: settings.applicationName,
						dexfApplicationId: settings.dexfApplicationId,
						dexfConfigurationVersion: settings.dexfConfigurationVersion,
					});

					const icf = configurationService.getICF(
						sofaConfiguration,
						products,
						sofaModels,
						sofaGroups,
						rangeSettings
					);

					const vpcConfiguration = await Promise.race([
						timeoutAfter(15),
						vpcService.saveConfiguration(
							sofaConfiguration,
							[...shoppingItems, ...additionalCoverItems],
							screenshot,
							icf
						),
					]);

					if (vpcConfiguration) {
						const iframeMessage = 'upptacka_summaryclicked:' + vpcConfiguration?.configurationId;

						setTimeout(() => {
							window.parent.postMessage(
								{
									method: iframeMessage,
								},
								'*'
							);
							setUpptackaLoading(false);
						}, 2500);
					} else {
						throw new Error('no configuration or timeout');
					}

					nextClicked = false;
				} catch (error) {
					setUpptackaLoading(false);
					dispatch(screenshotSuccess(null));
					dispatch(setUpptackaSaveFail(true, error));
					dispatch(setCurrentLightbox('failed-prompt'));
					nextClicked = false;
				}
			};
			sendUpptackaEvent();
		}
	}, [screenshot, screenshotIsPending]);

	const usePreviousShoppingItems = (value: ShoppingItem[] | null) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const shoppingItemsRef: any = useRef();
		useEffect(() => {
			shoppingItemsRef.current = value;
		});
		return shoppingItemsRef.current;
	};
	const prevShoppingItems: ShoppingItem[] | null = usePreviousShoppingItems(shoppingItems);

	useEffect(() => {
		getShoppingProducts();
		if (kompisSettings) {
			if (
				(shoppingItems && prevShoppingItems === null) ||
				JSON.stringify(shoppingItems) !== JSON.stringify(prevShoppingItems) ||
				shoppingItems.length === 0
			) {
				const totalPriceEvent = async () => {
					const products = await getShoppingProducts();
					const totalPrice = TotalPriceCalculator.calculateMultiple(
						products,
						kompisSettings.localisation.dateFormat
					);
					const currencySymbol =
						kompisSettings.kompis.priceSettings.currencySymbolAlt ||
						kompisSettings.localisation.currencyCode ||
						'';
					const totalVoice = totalPrice
						.map(
							(price) =>
								PriceFormatterUtility.format(PriceUtility.getPrice(price), kompisSettings)
									.filter(({ type }) => type !== 'currency')
									.map(({ value }) => value)
									.join('') + currencySymbol
						)
						.join('');
					if (totalPriceVoice !== totalVoice) {
						dispatch(setTotalPriceVoice(totalVoice));
					}
				};
				totalPriceEvent();
			}
		}
	}, [shoppingItems, coverOnly]);

	useEffect(() => {
		const onResize = (element: ResizeObserverEntry[]): void => {
			dispatch(setFooterHeight((element[0].target as HTMLElement).offsetHeight));
		};
		const resizeObserver = new ResizeObserver(onResize);
		const element = document.getElementById('footer');
		if (element) {
			dispatch(setFooterHeight(element.offsetHeight));
			resizeObserver.observe(element);
		}
		return (): void => {
			if (element) {
				resizeObserver.unobserve(element);
			}
		};
	}, []);

	return (
		<>
			<StyledFooter
				id="footer"
				deviceType={deviceType}
				orientation={orientation}
				platform={platform}
				writeDirection={writeDirection}
			>
				{deviceType !== 'mobile' ? <ActionButtonsContainer /> : null}
				{kompisSettings && kompisTranslations && (
					<KompisMiniConfigurationSummary
						theme={SkapaTheme}
						settings={kompisSettings}
						translations={kompisTranslations.kompis}
						shoppingProducts={shoppingProducts}
						disabled={enableMiniConfigurationSummary && shoppingItems.length > 0 ? false : true}
						disableLinks={platform === 'kiosk'}
						animatedPrice={true}
						hideWhatsIncludedIcon={true}
						loading={upptackaLoading}
						buttonLabel={localeService.getTranslation('totalPriceSummaryButton')}
						visibleModal={visibleModal}
						onModalClose={onModalClose}
						onModalOpen={(e: CustomEvent): void => {
							onModalOpen && onModalOpen(e);
						}}
						onNext={(): void =>
							onNextClick(
								dispatch,
								platform,
								unfinishedSections,
								missingCoverSections,
								backrestsMissingCushion,
								noValidConnection,
								isInUpptackaPlatform,
								products
							)
						}
						buttonSize={ButtonSizeEnum.medium}
					></KompisMiniConfigurationSummary>
				)}
				<div aria-live="polite" aria-relevant="all" style={{ fontSize: 0, position: 'absolute' }}>
					{totalPriceVoice ? <p>{totalPriceVoice}</p> : null}
				</div>
			</StyledFooter>
		</>
	);
};

export default FooterContainer;
